import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@headlessui/react'
import { Title, Text, Button, TextInput, Textarea } from '@tremor/react'
import { XMarkIcon, DocumentTextIcon } from '@heroicons/react/24/outline'
import { sanitizeUserInput } from '../../utils/security'

function QuestionnaireInitialSetupModal({ isOpen, onClose, onContinue }) {
    const { t } = useTranslation()
    const [formData, setFormData] = useState({
        title: '',
        description: ''
    })
    const [errors, setErrors] = useState({})

    if (!isOpen) return null

    const validateForm = () => {
        const newErrors = {}
        
        if (!formData.title.trim()) {
            newErrors.title = 'Title is required'
        } else if (formData.title.trim().length < 3) {
            newErrors.title = 'Title must be at least 3 characters long'
        } else if (formData.title.trim().length > 255) {
            newErrors.title = 'Title must not exceed 255 characters'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        
        if (!validateForm()) {
            return
        }

        // Sanitize the input data
        const sanitizedData = {
            title: sanitizeUserInput(formData.title.trim()),
            description: formData.description.trim() ? sanitizeUserInput(formData.description.trim()) : ''
        }

        // Pass the data to the parent component
        onContinue(sanitizedData)
        
        // Reset form for next use
        setFormData({ title: '', description: '' })
        setErrors({})
    }

    const handleCancel = () => {
        // Reset form data
        setFormData({ title: '', description: '' })
        setErrors({})
        onClose()
    }

    const handleTitleChange = (e) => {
        setFormData(prev => ({ ...prev, title: e.target.value }))
        // Clear title error when user starts typing
        if (errors.title) {
            setErrors(prev => ({ ...prev, title: null }))
        }
    }

    const handleDescriptionChange = (e) => {
        setFormData(prev => ({ ...prev, description: e.target.value }))
    }

    return (
        <Dialog 
            open={isOpen} 
            onClose={handleCancel}
            className="relative z-50"
        >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
            
            <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center">
                    <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center space-x-3">
                                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-iavisus-100">
                                    <DocumentTextIcon className="h-6 w-6 text-white" />
                                </div>
                                <div>
                                    <Dialog.Title as={Title} className="text-lg font-semibold text-gray-900">
                                        Create New Questionnaire
                                    </Dialog.Title>
                                    <Text className="text-sm text-gray-500">
                                        Let's start with the basics
                                    </Text>
                                </div>
                            </div>
                            <button
                                onClick={handleCancel}
                                className="rounded-lg p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Title Input */}
                            <div>
                                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                                    Questionnaire Title *
                                </label>
                                <TextInput
                                    id="title"
                                    value={formData.title}
                                    onChange={handleTitleChange}
                                    placeholder="Enter a descriptive title for your questionnaire"
                                    className={`w-full ${errors.title ? 'border-red-500' : ''}`}
                                    maxLength={255}
                                    autoFocus
                                />
                                {errors.title && (
                                    <Text className="text-red-500 text-sm mt-1">
                                        {errors.title}
                                    </Text>
                                )}
                                <Text className="text-gray-400 text-xs mt-1">
                                    {formData.title.length}/255 characters
                                </Text>
                            </div>

                            {/* Description Input */}
                            <div>
                                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                                    Description (Optional)
                                </label>
                                <Textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={handleDescriptionChange}
                                    placeholder="Brief description of what this questionnaire is about"
                                    rows={3}
                                    className="w-full resize-none"
                                    maxLength={500}
                                />
                                <Text className="text-gray-400 text-xs mt-1">
                                    {formData.description.length}/500 characters
                                </Text>
                            </div>

                            {/* Info Message */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <Text className="text-blue-800 text-sm">
                                    <strong>Next steps:</strong> After providing the title, you'll be able to add questions, 
                                    configure settings, upload media, and customize your questionnaire.
                                </Text>
                            </div>

                            {/* Buttons */}
                            <div className="flex justify-end space-x-3 pt-4">
                                <Button
                                    variant="secondary"
                                    onClick={handleCancel}
                                    type="button"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                                    disabled={!formData.title.trim()}
                                >
                                    Continue to Builder
                                </Button>
                            </div>
                        </form>
                    </Dialog.Panel>
                </div>
            </div>
        </Dialog>
    )
}

export default QuestionnaireInitialSetupModal